import React from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  ApplyButton,
  CompHalf,
  CompHalfTag,
  CompSplit,
  CountBadge,
  GridCell,
  MasterCellStack,
  PlacedShowWrapper,
  RemoveButton,
  ShowBoxCurator,
  ShowBoxRoot,
  ShowBoxTitle,
  StateButton,
} from "./ScheduleBoard.styles";
import ShowBox from "./ShowBox";
import { sameFrequency } from "@src/utils/scheduleFrequency";
import {
  ApplyRequest,
  MasterGridItem,
  PlacedItem,
  ShowState,
  placedItemShowId,
  placedItemTitle,
} from "./types";

interface ComparisonCellProps {
  cellId: string;
  monthStart: boolean;
  // Board (editable) side.
  occupant?: PlacedItem;
  occupantState?: ShowState;
  occupantStateKey?: string;
  occupantCount?: number;
  occupantNextAirDate?: string;
  // Master-schedule (read-only) side.
  masterItems: MasterGridItem[];
  whiteBlocks?: boolean;
  activeOriginCellId: string | null;
  canApply?: boolean;
  onRemove: (cellId: string) => void;
  onOpenStateMenu: (
    key: string,
    current: ShowState,
    x: number,
    y: number
  ) => void;
  onApply?: (request: ApplyRequest) => void;
}

const ComparisonCell = ({
  cellId,
  monthStart,
  occupant,
  occupantState,
  occupantStateKey,
  occupantCount,
  occupantNextAirDate,
  masterItems,
  whiteBlocks,
  activeOriginCellId,
  canApply,
  onRemove,
  onOpenStateMenu,
  onApply,
}: ComparisonCellProps): React.JSX.Element => {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell:${cellId}`,
    data: { type: "cell", cellId },
  });

  const isOccupied = Boolean(occupant);
  const invalidOver = isOver && isOccupied && activeOriginCellId !== cellId;
  const validOver = isOver && !invalidOver;

  // Board and master agree when the slot holds a single show on each side and
  // it is the same show — then we render one unified block instead of two
  // identical halves.
  const occupantShowId = occupant ? placedItemShowId(occupant) : null;
  const unified =
    !!occupantShowId &&
    masterItems.length === 1 &&
    masterItems[0].key === occupantShowId;

  // Two different blocks share this slot: the board side proposes one show and
  // the master side already schedules another. The master half is highlighted
  // orange and offers "Apply" to push the board show into that master slot.
  const differ = isOccupied && masterItems.length > 0 && !unified;
  // Apply targets the primary (first) master block and needs a real board show
  // (notes have no documentId to schedule) plus a known slot time to schedule
  // into.
  const primaryMaster = masterItems[0];
  const applyReady =
    Boolean(canApply) &&
    differ &&
    !!occupantShowId &&
    !!occupant &&
    !!primaryMaster?.slotTime;

  // Apply is only actionable when the board and master shows air on the same
  // cadence. The button still appears so the reason is discoverable, but it is
  // disabled (with an explanatory tooltip) when the frequencies differ.
  const boardFrequency =
    occupant?.kind === "show" ? occupant.show.frequency : null;
  const frequencyMatches = sameFrequency(
    boardFrequency,
    primaryMaster?.frequency
  );

  const boardBlock = occupant ? (
    <PlacedShowWrapper>
      {occupantStateKey && (
        <StateButton
          type="button"
          aria-label={`Change state of ${placedItemTitle(occupant)}`}
          onPointerDown={(e): void => e.stopPropagation()}
          onClick={(e): void => {
            e.stopPropagation();
            onOpenStateMenu(
              occupantStateKey,
              occupantState ?? "regular",
              e.clientX,
              e.clientY
            );
          }}
        >
          ✳
        </StateButton>
      )}
      <ShowBox
        id={`placed:${cellId}`}
        title={placedItemTitle(occupant)}
        subtitle={
          occupant.kind === "show"
            ? occupant.show.curators?.[0]?.name
            : undefined
        }
        state={occupantState}
        count={occupant.kind === "show" ? occupantCount : undefined}
        nextAirDate={occupantNextAirDate}
        whiteBlocks={whiteBlocks}
        data={{ type: "placed", item: occupant, fromCellId: cellId }}
      />
      <RemoveButton
        type="button"
        aria-label={`Remove ${placedItemTitle(occupant)}`}
        onPointerDown={(e): void => e.stopPropagation()}
        onClick={(e): void => {
          e.stopPropagation();
          onRemove(cellId);
        }}
      >
        ×
      </RemoveButton>
    </PlacedShowWrapper>
  ) : null;

  const masterStack =
    masterItems.length > 0 ? (
      <MasterCellStack>
        {masterItems.map((item) => (
          <ShowBoxRoot
            key={item.key}
            as="div"
            $readOnly
            $state={item.state}
            $whiteBlocks={whiteBlocks}
            title={
              item.subtitle ? `${item.title} — ${item.subtitle}` : item.title
            }
          >
            <ShowBoxTitle>
              {item.title}
              {item.count > 0 ? <CountBadge>({item.count})</CountBadge> : null}
            </ShowBoxTitle>
            {item.subtitle ? (
              <ShowBoxCurator>{item.subtitle}</ShowBoxCurator>
            ) : null}
          </ShowBoxRoot>
        ))}
      </MasterCellStack>
    ) : null;

  return (
    <GridCell
      ref={setNodeRef}
      $isOver={unified && validOver}
      $invalidOver={unified && invalidOver}
      $occupied={isOccupied || masterItems.length > 0}
      $monthStart={monthStart}
    >
      {unified ? (
        boardBlock
      ) : (
        <CompSplit>
          <CompHalf $isOver={validOver} $invalidOver={invalidOver}>
            {boardBlock ?? <CompHalfTag>Board</CompHalfTag>}
          </CompHalf>
          <CompHalf $orange={differ}>
            {applyReady && occupant && primaryMaster?.slotTime ? (
              <ApplyButton
                type="button"
                disabled={!frequencyMatches}
                title={
                  frequencyMatches
                    ? undefined
                    : "Board and master shows must have the same frequency to apply"
                }
                aria-label={`Schedule ${placedItemTitle(
                  occupant
                )} into this master slot`}
                onPointerDown={(e): void => e.stopPropagation()}
                onClick={(e): void => {
                  e.stopPropagation();
                  if (!frequencyMatches) return;
                  onApply?.({
                    cellId,
                    boardItem: occupant,
                    masterTitle: primaryMaster.title,
                    masterShowId: primaryMaster.key,
                    slotTime: primaryMaster.slotTime as string,
                  });
                }}
              >
                Apply
              </ApplyButton>
            ) : null}
            {masterStack ?? <CompHalfTag>Master</CompHalfTag>}
          </CompHalf>
        </CompSplit>
      )}
    </GridCell>
  );
};

export default ComparisonCell;
